import React, { useEffect, useMemo, useState } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { ArrowLeft, FileText, Search, X, RefreshCw, Download } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { dcGetBlob } from "../../services/districtCourtApi";
import { exportSingleDistrictCourtOrder } from "./districtCourtOrderFiles";

// User-exported cases live in these Firestore collections (keyed by owner).
const CASE_COLLECTIONS = ["pending", "disposed", "decided"];

// Normalize a case-number string to alphanumerics so spacing/punctuation
// differences ("Cri.Bail Appln./1189/2026" vs "CRIBAILAPPLN/1189/2026") don't
// break the match.
const normalizeCaseNo = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

// The "<number>/<year>" core of a case-number string — the last two
// "/"-separated segments. Lets us match when the type prefix differs but the
// registration number + year are identical.
const caseNoCore = (s) => {
  const parts = String(s || "")
    .split("/")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${normalizeCaseNo(parts[parts.length - 2])}/${normalizeCaseNo(parts[parts.length - 1])}`;
  }
  return "";
};

// District Court "Court Orders" results. The search endpoints return order
// records parsed from the eCourts court-order table, each shaped like:
//   { srNo, caseInfo, partyNames, orderDate, ordersText, courtName,
//     pdf_params: { normal_v, case_val, court_code, filename, appFlag } }
// "View" streams the order PDF through the backend proxy (GET
// /api/courtorders/order-pdf), which is gated behind the search session.
function DistrictCourtOrdersListScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { globalUser } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [loadingKey, setLoadingKey] = useState(null);
  const [exportingKey, setExportingKey] = useState(null);

  const result = location.state?.data || {};
  const orders = result?.orders || [];
  const metadata = result?.metadata || {};
  const sessionId = location.state?.sessionId || "";

  useEffect(() => {
    if (!location.state) {
      navigate("/portal/fetchfromdistrictcourt", { replace: true });
    }
  }, [location.state, navigate]);

  const orderKey = (order, idx) =>
    `${order.caseInfo || ""}__${order.orderDate || ""}__${order.srNo ?? idx}`;

  const filteredOrders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) =>
      [o.caseInfo, o.partyNames, o.orderDate, o.courtName, o.ordersText]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [orders, searchQuery]);

  const handleViewOrder = async (order, idx) => {
    const pdf = order.pdf_params;
    if (!pdf || !pdf.normal_v || !pdf.case_val || !pdf.court_code || !pdf.filename) {
      toast.error("This order has no downloadable document.");
      return;
    }
    if (!sessionId) {
      toast.error("Session expired — please run the search again.");
      return;
    }
    const key = orderKey(order, idx);
    setLoadingKey(key);
    try {
      const res = await dcGetBlob("/api/courtorders/order-pdf", {
        sessionId,
        normal_v: pdf.normal_v,
        case_val: pdf.case_val,
        court_code: pdf.court_code,
        filename: pdf.filename,
        appFlag: pdf.appFlag || "",
      });
      if (!res.ok) {
        toast.error(res.error || "No order PDF available from the portal.", {
          duration: 5000,
        });
        return;
      }
      if (!res.blob || res.blob.size === 0) {
        toast.error("No order PDF available from the portal.");
        return;
      }
      const url = URL.createObjectURL(res.blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoke after a delay so the new tab has time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.message || "Failed to open order PDF", { duration: 5000 });
    } finally {
      setLoadingKey(null);
    }
  };

  // Find the user's already-exported case that this order belongs to, matched by
  // case number (the order has no CNR). Query owner-only (a single-field index
  // that already exists) and match client-side, so no composite index is needed.
  const findExportedCase = async (order) => {
    const uid = globalUser?.uid;
    if (!uid) return null;
    const targetFull = normalizeCaseNo(order.caseInfo);
    const targetCore = caseNoCore(order.caseInfo);
    for (const col of CASE_COLLECTIONS) {
      const snap = await getDocs(
        query(collection(db, col), where("owner", "==", uid))
      );
      const match = snap.docs.find((d) => {
        const c = d.data();
        const full = normalizeCaseNo(c.caseNo);
        const core = caseNoCore(c.caseNo);
        return (
          (targetFull && full === targetFull) ||
          (targetCore && core && core === targetCore)
        );
      });
      if (match) return { id: match.id, ...match.data(), __collection: col };
    }
    return null;
  };

  // Export ONE order into its already-exported case. If the case isn't exported
  // yet, tell the user to export it first. Re-exporting the same order is a
  // no-op (deduped by file name inside exportSingleDistrictCourtOrder).
  const handleExportOrder = async (order, idx) => {
    if (!globalUser?.uid) {
      toast.error("Please sign in to export orders.");
      return;
    }
    const pdf = order.pdf_params;
    if (!pdf || !pdf.normal_v || !pdf.filename) {
      toast.error("This order has no downloadable document.");
      return;
    }
    if (!sessionId) {
      toast.error("Session expired — please run the search again.");
      return;
    }
    const key = orderKey(order, idx);
    setExportingKey(key);
    try {
      const caseDoc = await findExportedCase(order);
      if (!caseDoc) {
        toast.error(
          `Please export the case first (Case No: ${order.caseInfo || "—"}) to export this order.`,
          { duration: 7000 }
        );
        return;
      }
      const exportResult = await exportSingleDistrictCourtOrder({
        order,
        caseDoc,
        caseCollection: caseDoc.__collection,
        ownerId: globalUser.uid,
        sessionId,
      });
      if (exportResult.status === "skipped") {
        toast("Order already exported to this case.", { icon: "ℹ️", duration: 5000 });
      } else if (exportResult.status === "no-pdf") {
        toast.error(
          "Order PDF unavailable from the portal — re-run the search and try again.",
          { duration: 6000 }
        );
      } else {
        toast.success(`Order exported to case ${caseDoc.caseNo || ""}.`, { duration: 5000 });
      }
    } catch (err) {
      toast.error(err?.message || "Failed to export order", { duration: 6000 });
    } finally {
      setExportingKey(null);
    }
  };

  return (
    <>
                        <div className="relative min-h-screen flex flex-col">
          <div className="mb-4"><FindCaseStepper current={3} /></div>
        <main className="px-4 md:px-16 py-8">
          {/* Header */}
          <div className="relative z-10 flex items-center justify-between p-4 flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate("/portal/fetchfromdistrictcourt")}
              className="flex items-center md:ml-12"
            >
              <ArrowLeft style={{ color: "#334155" }} />
              <h1 className="text-yellow-500 ml-4 font-bold text-2xl">Court Orders</h1>
            </button>
            <div className="text-sm text-slate-600 md:mr-12">
              {orders.length} order{orders.length !== 1 ? "s" : ""} found
              {metadata?.courtName ? ` · ${metadata.courtName}` : ""}
            </div>
          </div>

          <div className="flex-grow mt-2 mb-12 flex flex-col items-center relative z-10">
            <div className="w-full lg:w-5/6 bg-white border border-slate-200 shadow-sm text-slate-800 p-5 rounded-[30px]">
              {/* Search filter */}
              <div className="flex items-center gap-2 mb-4 px-2">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search orders (case, party, date…)"
                    className="bg-white text-black py-1.5 pl-9 pr-8 rounded text-sm w-full outline-none focus:ring-2 focus:ring-yellow-500 border border-gray-300"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {orders.length === 0 ? (
                <div className="text-center py-12 text-slate-600">
                  <p className="text-lg">No court orders found</p>
                  <button
                    type="button"
                    onClick={() => navigate("/portal/fetchfromdistrictcourt")}
                    className="text-yellow-400 hover:underline mt-2 text-sm"
                  >
                    Back to search
                  </button>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-10 text-slate-600">
                  <p className="text-lg">No orders match your search</p>
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="text-yellow-400 hover:underline mt-2 text-sm"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="p-3 text-left">#</th>
                        <th className="p-3 text-left">Case</th>
                        <th className="p-3 text-left">Parties</th>
                        <th className="p-3 text-left">Order Date</th>
                        <th className="p-3 text-left">Court</th>
                        <th className="p-3 text-left">Order</th>
                        <th className="p-3 text-left">Export</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order, idx) => {
                        const key = orderKey(order, idx);
                        const isLoading = loadingKey === key;
                        const hasPdf =
                          order.pdf_params &&
                          order.pdf_params.normal_v &&
                          order.pdf_params.filename;
                        return (
                          <tr
                            key={key}
                            className="border-b border-gray-700 hover:bg-blue-900/20 transition-colors duration-150"
                          >
                            <td className="p-3">{order.srNo || idx + 1}</td>
                            <td className="p-3">{order.caseInfo || "—"}</td>
                            <td className="p-3">{order.partyNames || "—"}</td>
                            <td className="p-3">{order.orderDate || "—"}</td>
                            <td className="p-3 text-xs text-slate-600">{order.courtName || "—"}</td>
                            <td className="p-3">
                              <button
                                type="button"
                                onClick={() => handleViewOrder(order, idx)}
                                disabled={isLoading || !hasPdf}
                                className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded py-1 px-3 font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title={hasPdf ? "View order PDF" : "No document available"}
                              >
                                {isLoading ? (
                                  <RefreshCw className="animate-spin h-3.5 w-3.5" />
                                ) : (
                                  <FileText className="h-3.5 w-3.5" />
                                )}
                                {isLoading ? "Opening…" : "View"}
                              </button>
                            </td>
                            <td className="p-3">
                              <button
                                type="button"
                                onClick={() => handleExportOrder(order, idx)}
                                disabled={exportingKey === key || !hasPdf}
                                className="inline-flex items-center gap-1.5 bg-green-600 text-white rounded py-1 px-3 font-semibold text-xs hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Export this order into its already-exported case"
                              >
                                {exportingKey === key ? (
                                  <RefreshCw className="animate-spin h-3.5 w-3.5" />
                                ) : (
                                  <Download className="h-3.5 w-3.5" />
                                )}
                                {exportingKey === key ? "Exporting…" : "Export"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default DistrictCourtOrdersListScreen;
